import { modal, button, severityTicks, sheetActions } from "../../design/components.js";
import type { CreateInput } from "./help-publish.js";

export interface RequestSheetCtx {
  t: (k: string, p?: Record<string, string | number>) => string;
  defaultCell: string;
  onSubmit: (input: CreateInput) => Promise<void>;
  onClose: () => void;
}

const NEEDS = ["medical", "rescue", "transport", "shelter", "supplies", "hands", "other"] as const;

/** Create-request bottom sheet (GUI §4 "Creating a request") — report-sheet pattern:
 *  need-type → what/how-many → urgency. One screen feel, big targets, authored under stress. */
export function requestSheet(ctx: RequestSheetCtx): HTMLElement {
  const t = ctx.t;
  const body = document.createElement("div");
  body.className = "request-sheet__body";
  const sheet = modal({ title: t("help.create.title"), content: body, onClose: ctx.onClose, sheetId: "create-request" });

  let need = "";
  let headline = "";
  let people: number | undefined;
  let urgency = 2;

  const render = (el: HTMLElement) => body.replaceChildren(el);

  async function submit() {
    try {
      await ctx.onSubmit({ need, headline: headline.trim(), urgency, cell: ctx.defaultCell, ...(people != null ? { people } : {}) });
      render(confirmation());
    } catch {
      const err = document.createElement("p");
      err.className = "bx-body request-sheet__error";
      err.textContent = t("help.create.error");
      body.append(err);
    }
  }

  function confirmation(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.setAttribute("data-step", "done");
    const h = document.createElement("h2");
    h.className = "bx-display";
    h.textContent = t("help.create.done.title");
    const p = document.createElement("p");
    p.className = "bx-body";
    p.textContent = t("help.create.done.body");
    const close = button({ label: t("help.create.done.close"), variant: "primary", onClick: ctx.onClose });
    close.setAttribute("data-action", "done-close");
    wrap.append(h, p, close);
    return wrap;
  }

  function stepNeed(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.setAttribute("data-step", "need");
    const h = document.createElement("h2");
    h.className = "bx-display";
    h.textContent = t("help.create.step.need");
    wrap.append(h);
    const grid = document.createElement("div");
    grid.className = "request-sheet__needs";
    for (const n of NEEDS) {
      const b = button({
        label: t(`need.${n}`),
        variant: n === need ? "primary" : "secondary",
        onClick: () => { need = n; render(stepNeed()); },
      });
      b.setAttribute("data-need", n);
      grid.append(b);
    }
    wrap.append(grid);
    const next = button({ label: t("help.create.next"), variant: "primary", onClick: () => render(stepWhat()) });
    next.setAttribute("data-action", "next");
    (next as HTMLButtonElement).disabled = !need;
    wrap.append(sheetActions({ primary: next }));
    return wrap;
  }

  function stepWhat(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.setAttribute("data-step", "what");
    const h = document.createElement("h2");
    h.className = "bx-display";
    h.textContent = t("help.create.step.what");
    wrap.append(h);

    const headlineInput = document.createElement("input");
    headlineInput.type = "text";
    headlineInput.className = "bx-input";
    headlineInput.maxLength = 120;
    headlineInput.placeholder = t("help.create.headlinePlaceholder");
    headlineInput.setAttribute("data-headline", "");
    headlineInput.value = headline;
    const peopleInput = document.createElement("input");
    peopleInput.type = "number";
    peopleInput.className = "bx-input";
    peopleInput.min = "1";
    peopleInput.placeholder = t("help.create.peoplePlaceholder");
    peopleInput.setAttribute("data-people", "");

    const back = button({ label: t("help.create.back"), variant: "secondary", onClick: () => render(stepNeed()) });
    back.setAttribute("data-action", "back");
    const next = button({ label: t("help.create.next"), variant: "primary", onClick: () => render(stepUrgency()) });
    next.setAttribute("data-action", "next");
    const sync = () => { (next as HTMLButtonElement).disabled = headline.trim().length === 0; };
    headlineInput.addEventListener("input", () => { headline = headlineInput.value; sync(); });
    peopleInput.addEventListener("input", () => {
      const n = parseInt(peopleInput.value, 10);
      people = Number.isFinite(n) && n > 0 ? n : undefined;
    });
    wrap.append(headlineInput, peopleInput, sheetActions({ back, primary: next }));
    sync();
    return wrap;
  }

  function stepUrgency(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.setAttribute("data-step", "urgency");
    const h = document.createElement("h2");
    h.className = "bx-display";
    h.textContent = t("help.create.step.urgency");
    wrap.append(h);
    const ticks = document.createElement("div");
    ticks.className = "request-sheet__urgency";
    const label = document.createElement("div");
    label.className = "bx-microlabel";
    label.textContent = t("help.create.urgencyLabel");
    ticks.append(label);
    for (let u = 1; u <= 3; u++) {
      const b = button({
        label: String(u),
        variant: u === urgency ? "primary" : "secondary",
        onClick: () => { urgency = u; render(stepUrgency()); },
      });
      b.setAttribute("data-urgency", String(u));
      ticks.append(b);
    }
    ticks.append(severityTicks(urgency, 3));
    wrap.append(ticks);
    const back = button({ label: t("help.create.back"), variant: "secondary", onClick: () => render(stepWhat()) });
    back.setAttribute("data-action", "back");
    const send = button({ label: t("help.create.submit"), variant: "primary", onClick: () => void submit() });
    send.setAttribute("data-action", "submit-request");
    wrap.append(sheetActions({ back, primary: send }));
    return wrap;
  }

  render(stepNeed());
  return sheet;
}
